if (!customElements.get('product-mobile-sticky-cart')) {
  customElements.define('product-mobile-sticky-cart', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.info = this.closest('product-info');
      this.sourceId = this.dataset.sourceButton;
      this.quantityId = this.dataset.sourceQuantity;
      this.trigger = this.querySelector('[data-open="purchase"]');
      this.confirm = this.querySelector('[data-action="add"]');
      this.quantity = this.querySelector('[data-quantity]');
      this.panels = [...this.querySelectorAll('dialog')];
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.share = this.info?.querySelector('z-product-share');
      this.related = this.info?.querySelector('[data-bs-related-products]');
      this.description = this.info?.querySelector('.bs-product-description-below');
      this.positions = new Map();
      [this.share, this.related].filter(Boolean).forEach((element) => {
        const marker = document.createComment('mobile-actions-position');
        element.before(marker);
        this.positions.set(element, marker);
      });
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.quantity.addEventListener('change', () => this.commitQuantity(), options);
      this.info?.addEventListener('change', () => this.scheduleSync(), options);
      this.mobile.addEventListener('change', () => this.updateLayout(), options);
      this.panels.forEach((panel) => {
        panel.addEventListener('click', (event) => {
          if (event.target !== panel) return;
          const box = panel.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) panel.close();
        }, options);
        panel.addEventListener('close', () => {
          this.querySelector(`[data-open="${panel.dataset.panel}"]`)?.setAttribute('aria-expanded', 'false');
          this.share?.closeMailMenu?.();
        }, options);
      });
      this.observer = new MutationObserver(() => this.scheduleSync());
      const watched = [this.sourceButton, this.sourceQuantity, this.info?.querySelector('[data-bs-product-subtotal]'), this.info?.querySelector('[id^="price-"]'), this.info?.querySelector('.product-form__error-message-wrapper')];
      watched.filter(Boolean).forEach((element) => this.observer.observe(element, { subtree: true, childList: true, characterData: true, attributes: true }));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== this.info?.dataset.section) return;
          if (event.data.variant) this.dataset.price = String(event.data.variant.price);
          this.scheduleSync();
        });
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.panels.forEach((panel) => { if (panel.open) panel.close(); });
          this.scheduleSync();
        });
      }
      this.updateLayout();
      this.resizeObserver = new ResizeObserver(() => {
        if (this.mobile.matches && !this.hidden) document.documentElement.style.setProperty('--bs-mobile-actions-height', `${this.querySelector('.bs-mobile-sticky-cart__inner').offsetHeight + 17}px`);
      });
      this.resizeObserver.observe(this.querySelector('.bs-mobile-sticky-cart__inner'));
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.unsubscribeVariant?.();
      this.unsubscribeCart?.();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.panels?.forEach((panel) => { if (panel.open) panel.close(); });
      this.restorePositions();
      this.positions?.forEach((marker) => marker.remove());
      this.info?.classList.remove('bs-mobile-actions-ready');
      document.documentElement.classList.remove('bs-mobile-sticky-cart-active');
      document.documentElement.style.removeProperty('--bs-mobile-actions-height');
    }
    get sourceButton() { return document.getElementById(this.sourceId); }
    get sourceQuantity() { return document.getElementById(this.quantityId); }
    restorePositions() {
      this.positions?.forEach((marker, element) => { if (marker.parentNode) marker.after(element); });
    }
    updateLayout() {
      const ready = this.mobile.matches && !!this.sourceButton && typeof HTMLDialogElement !== 'undefined' && typeof this.panels[0].showModal === 'function';
      this.hidden = !ready;
      this.info?.classList.toggle('bs-mobile-actions-ready', ready);
      document.documentElement.classList.toggle('bs-mobile-sticky-cart-active', ready);
      if (ready) {
        if (this.share) this.querySelector('[data-share-slot]').append(this.share);
        if (this.related && this.description) this.description.after(this.related);
      } else {
        this.panels.forEach((panel) => { if (panel.open) panel.close(); });
        this.restorePositions();
        document.documentElement.style.removeProperty('--bs-mobile-actions-height');
      }
      this.querySelector('[data-open="share"]').hidden = !this.share;
      this.sync();
    }
    scheduleSync() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
    }
    setText(selector, text) {
      const element = this.querySelector(selector);
      if (element && element.textContent !== text) element.textContent = text;
    }
    sync() {
      const source = this.sourceButton;
      if (!source) return;
      const blocked = source.disabled || source.getAttribute('aria-disabled') === 'true';
      const loading = source.classList.contains('loading');
      this.trigger.disabled = blocked || loading;
      this.confirm.disabled = blocked || loading;
      const sourceLabel = source.querySelector('span')?.textContent.trim();
      this.setText('[data-open="purchase"] span', sourceLabel || window.variantStrings?.addToCart || '');
      this.setText('[data-action="add"]', blocked && !loading ? sourceLabel : this.dataset.addLabel);
      this.confirm.setAttribute('aria-busy', String(loading));
      const quantity = this.sourceQuantity;
      this.querySelector('.bs-mobile-sticky-cart__quantity').hidden = !quantity;
      if (quantity) {
        for (const name of ['min', 'max', 'step']) {
          const value = quantity.getAttribute(name);
          if (value === null) this.quantity.removeAttribute(name);
          else if (this.quantity.getAttribute(name) !== value) this.quantity.setAttribute(name, value);
        }
        if (document.activeElement !== this.quantity) this.quantity.value = quantity.value;
        const value = Number(quantity.value || 1);
        this.querySelector('[data-action="minus"]').disabled = value <= Number(quantity.min || 1);
        this.querySelector('[data-action="plus"]').disabled = quantity.max !== '' && value >= Number(quantity.max);
      }
      const picker = this.info.querySelector('variant-selects');
      const choices = picker ? [...picker.querySelectorAll('input:checked, select option:checked')].map((element) => element.value).join(' / ') : '';
      this.setText('[data-current-variant]', choices);
      const total = this.info.querySelector('[data-bs-subtotal-value]')?.textContent.trim();
      const amount = Number(quantity?.value || 1) * Number(this.dataset.price) / 100;
      this.setText('[data-total]', total || new Intl.NumberFormat(this.dataset.locale || 'ja', { style: 'currency', currency: this.dataset.currency || 'JPY' }).format(amount));
      const error = this.info.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message')?.textContent.trim() || '';
      this.setText('[data-error]', error);
      this.querySelector('[data-error]').hidden = !error;
    }
    commitQuantity() {
      const source = this.sourceQuantity;
      if (!source || !this.quantity.checkValidity()) return false;
      source.value = this.quantity.value;
      source.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
      return true;
    }
    openPanel(name) {
      const panel = this.querySelector(`[data-panel="${name}"]`);
      if (!panel || !this.mobile.matches) return;
      this.panels.forEach((other) => { if (other.open) other.close(); });
      this.sync();
      panel.showModal();
      this.querySelector(`[data-open="${name}"]`)?.setAttribute('aria-expanded', 'true');
    }
    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.dataset.open) { this.openPanel(button.dataset.open); return; }
      const action = button.dataset.action;
      if (action === 'close') { button.closest('dialog').close(); return; }
      if (action === 'top') {
        const gallery = this.info.querySelector('media-gallery') || this.info;
        const header = document.querySelector('.bs-nav')?.getBoundingClientRect().bottom || 0;
        const top = Math.max(0, gallery.getBoundingClientRect().top + window.scrollY - Math.max(0, header) - 8);
        window.scrollTo({ top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        return;
      }
      if (action === 'minus' || action === 'plus') {
        if (!this.commitQuantity()) { this.quantity.reportValidity(); return; }
        const source = this.sourceQuantity;
        source?.closest('quantity-input')?.querySelector(`button[name="${action}"]`)?.click();
        this.quantity.value = source?.value || '1';
        this.sync();
        return;
      }
      if (action === 'add') {
        if (this.sourceQuantity && !this.commitQuantity()) { this.quantity.reportValidity(); return; }
        const source = this.sourceButton;
        if (!source || source.disabled || source.getAttribute('aria-disabled') === 'true') return;
        source.click();
        this.sync();
      }
    }
  });
}
